import { formatPhoneForDisplay, LIMITS } from '@crew/shared';
import { useState } from 'react';
import { useAdmin } from '@/auth/AuthProvider';
import { adminApi } from '@/services/adminApi';
import { ApiClientError, errorMessage } from '@/services/apiError';
import { Button } from '../ui/Button';
import { Dialog } from '../ui/Dialog';
import { Notice } from '../ui/Notice';
import { TextField } from '../ui/TextField';
import { useToast } from '../ui/Toast';

/**
 * עריכת שם וטלפון של קבלן/עובד. משותף לכרטיס הנרשם בעבודה ולדף שלו במאגר.
 * הרשומה נשארת אותה רשומה: ההיסטוריה, השיחות והקישור האישי נשמרים,
 * והשינוי מתעדכן גם בעבודות הפעילות.
 */
export function EditPersonForm({
  person,
  onDone,
  onCancel,
}: {
  person: { id: string; fullName: string; phone: string };
  onDone: () => void;
  onCancel: () => void;
}) {
  const { user } = useAdmin();
  const toast = useToast();
  const [name, setName] = useState(person.fullName);
  const [phone, setPhone] = useState(formatPhoneForDisplay(person.phone));
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setErrors({});
    setFormError(null);
    try {
      const phoneChanged = phone.replace(/\D/g, '') !== formatPhoneForDisplay(person.phone).replace(/\D/g, '');
      await adminApi(user).updateContractor(person.id, {
        ...(name.trim() !== person.fullName ? { fullName: name } : {}),
        ...(phoneChanged ? { phone } : {}),
      });
      toast('הפרטים נשמרו');
      onDone();
    } catch (err) {
      if (err instanceof ApiClientError && err.fields) setErrors(err.fields);
      else setFormError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-3">
      <TextField label="שם מלא" value={name} onChange={(e) => setName(e.target.value)} error={errors.fullName} maxLength={LIMITS.maxNameLength} />
      <TextField
        label="טלפון"
        type="tel"
        inputMode="tel"
        dir="ltr"
        value={phone}
        onChange={(e) => setPhone(e.target.value)}
        error={errors.phone}
      />
      <p className="text-[13px] text-ink-2">
        השינוי מתעדכן גם בעבודות הפעילות. ההיסטוריה, השיחות והקישור האישי נשארים. מעכשיו המערכת תזהה את המספר החדש בהרשמה.
      </p>
      {formError && <Notice tone="error">{formError}</Notice>}
      <div className="flex gap-2">
        <Button block loading={busy} onClick={save}>
          שמירה
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          ביטול
        </Button>
      </div>
    </div>
  );
}

export function EditPersonDialog({
  open,
  person,
  onClose,
}: {
  open: boolean;
  person: { id: string; fullName: string; phone: string };
  onClose: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} title="עריכת פרטים">
      {/* key: טופס חדש בכל פתיחה, עם הערכים העדכניים */}
      {open && <EditPersonForm key={`${person.fullName}|${person.phone}`} person={person} onDone={onClose} onCancel={onClose} />}
    </Dialog>
  );
}
